import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { SocketClient } from '../web-socket/socket-client';
import { WSContext, type WSContextWrapper } from '../ws-context/ws-context';
import { useSession } from '../auth/use-session';

/**
 * Keeps the socket in step with the session.
 *
 * The original connected once on mount with a `[isSocketOpen]` dependency, so
 * the effect re-ran on every open and close and re-attached its listeners each
 * time. Here the effect depends on the token instead: the socket connects when
 * a session appears and drops when it goes, which is the only thing that
 * actually changes what the connection is.
 */
export function WSContextProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [isSocketOpen, setSocketIsOpen] = useState(false);

  useEffect(() => {
    const client = SocketClient.getInstance();

    const onOpen = () => setSocketIsOpen(true);
    const onClose = () => setSocketIsOpen(false);
    client.on('open', onOpen);
    client.on('close', onClose);

    if (session) {
      client.connect();
    } else {
      client.disconnect();
      setSocketIsOpen(false);
    }

    return () => {
      // Remove only our own listeners: removeAllListeners would also strip the
      // per-command subscriptions the pages register through this context.
      client.off('open', onOpen);
      client.off('close', onClose);
    };
  }, [session?.token]);

  const ctx: WSContextWrapper = useMemo(
    () => ({
      socketIsOpen: isSocketOpen,
      sendWSMessage(msg) {
        SocketClient.getInstance().sendMessage(msg);
      },
      subscribeMessage(cmd, callback) {
        SocketClient.getInstance().on(cmd, callback);
      },
      unsubscribeMessage(cmd) {
        SocketClient.getInstance().removeAllListeners(cmd);
      }
    }),
    [isSocketOpen]
  );

  return <WSContext.Provider value={ctx}>{children}</WSContext.Provider>;
}
